import { type RoomStateItemX } from '../../types/mfc'
import { getSafeNextOrPreviousSurrogate } from '../actions/getNextOrPreviousModel'
import { setFullWindow } from '../actions/layout'
import { loadUserIfNeeded } from '../actions/loadUserIfNeeded'
import { openPm, pausePMUnreadCount } from '../actions/message'
import { closeRoom, pinRoom, startAutoPlayInterval } from '../actions/room'
import { workerArchive } from '../actions/workerActions'
import { getModalState } from '../app-state/modals'
import {
    closeFloatingRoom,
    convertFloatingRoomToSticky,
    openFloatingRoom,
} from '../app-state/modals/state.floater'
import { ROOMSSTATE } from '../app-state/state'
import {
    _setRoomOpenState,
    getRoomsState,
    isModelViewable,
    setRoomIDState,
    setRoomLoungeState,
    setRoomLoungeSurrogateState,
    updateRoomState,
} from '../app-state/state.rooms'
import { waitForInitialModelList } from '../app-state/state.users'
import { FloaterSize, RoomView, SettingsType } from '../constants/enums'
import { authenticated } from '../mfcLogic/auth'
import {
    addToLoungeRecentModelsSession,
    enabledLounges,
} from '../mfcLogic/lounge'
import { isLounge } from '../mfcLogic/userProperties/user'
import { resetArchiveState } from '../state/state.archives'
import { setPMChatOpenState } from '../state/state.pm'
import {
    AppRouter,
    HISTORY,
    RouteHandler,
    RouteHandlerOptions,
    RouteHistoryItem,
    RouteInfo,
    getRouteSegments,
    routeConfigs,
} from '../state/state.router'
import {
    SEARCHSTATE,
    resetSearch,
    updateSearchQuery,
    updateSearchView,
    type URLSearchParam,
} from '../state/state.search'
import { SETTINGSSTATE } from '../state/state.settings'
import {
    USERSSTATE,
    getSlugAndId,
    idToSlug,
    meUser,
    slugToID,
} from '../state/state.users'
import { initVideo } from '../state/state.video'
import { isMobile as isClientMobile } from './browser'
import { TransitionTypes, motionTransition } from './transitions'

const _initiateNewRoom = (id: number) => {
    const roomStates = getRoomsState()
    // close all other non pinned rooms
    Object.values(roomStates).forEach(({ isPinned, id: existingID }) => {
        if (!existingID || id === existingID || isPinned) {
            return
        }

        closeRoom(existingID)
    })

    // initiate the new room's ID
    setRoomIDState(id)

    // set the room state to open
    _setRoomOpenState(true, id)
}

export const buildRoomRoute = (
    idOrSlug: string | number,
    surrogateIdOrSlug?: string | number,
) => {
    const [slug, id] = getSlugAndId(idOrSlug)
    const [surrogateSlug] = getSlugAndId(surrogateIdOrSlug)

    const roomStates = getRoomsState()

    const isIdLounge = isLounge(id)
    const { [id]: { loungeSurrogateID } = {} as RoomStateItemX } = roomStates
    return isIdLounge && (surrogateSlug || loungeSurrogateID)
        ? `/room/${slug}/${surrogateSlug ?? idToSlug[loungeSurrogateID]}`
        : `/room/${slug}`
}

export const closeRoomAndNavBack = async (
    closeRoom = true,
    coords?: { x: number; y: number },
) => {
    // console.log('[closeRoomAndNavBack]', {
    //     basePage: AppRouter.route.baseRoute,
    //     closeRoom,
    //     coords,
    // })
    return routerPush(AppRouter.route.baseRoute, { closeRoom, coords })
}

/** store the previous route in history and actuall call pushState.  shouldn't be used directly */
const route = (visibleRoute: string, replaceState: boolean) => {
    const PREVIOUS_ROUTE = HISTORY.lastRoute[HISTORY.lastRoute.length - 1]
    const newRoute: RouteHistoryItem = {
        visibleRoute,
        baseRoute: AppRouter.route.baseRoute,
    }
    if (
        PREVIOUS_ROUTE &&
        PREVIOUS_ROUTE.baseRoute === newRoute.baseRoute &&
        PREVIOUS_ROUTE.visibleRoute === newRoute.visibleRoute
    ) {
        // console.log('[route] bailing', { replaceState }, newRoute)
        return
    }

    HISTORY.lastRoute.push(newRoute)
    if (HISTORY.lastRoute.length > 2)
        HISTORY.lastRoute.splice(0, HISTORY.lastRoute.length - 2)
    // console.log(
    //     '[route]',
    //     { visibleRoute, replaceState },
    //     newRoute,
    //     PREVIOUS_ROUTE,
    // )
    if (typeof window !== 'undefined') {
        if (replaceState)
            return window.history.replaceState(newRoute, '', visibleRoute)
        else return window.history.pushState(newRoute, '', visibleRoute)
    }
}

const nonRoomShrinkCheck = (
    visibleRoute: string,
    routeInfo: RouteInfo,
    {
        closeRoom = false,
        coords,
        replaceState = false,
        initial = false,
    }: {
        closeRoom?: boolean
        coords?: { x: number; y: number }
        replaceState?: boolean
        initial?: boolean
    },
) => {
    const { currentRoute, currentSlug } = routeInfo
    // check if current route is room
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile
    // only worry about shrinking/closing a room if we're leaving a floater
    if (isCurrentRouteFloater) {
        // get current route's id, if room
        const currentRouteID = currentSlug ? slugToID[currentSlug] : null
        const unauthenticatedMessageRequest =
            isCurrentRoutePm && authenticated() === false

        const roomStates = getRoomsState()
        const modalState = getModalState()
        const { [currentRouteID]: currentRouteState } = roomStates
        const usableVideoID = isLounge(modalState.floating_room.id)
            ? currentRouteState?.loungeSurrogateID
            : modalState.floating_room.id
        if (
            modalState.floating_room.show &&
            modalState.floating_room.size === FloaterSize.Fullscreen
        ) {
            const { pinnedRooms } = ROOMSSTATE

            // hidden pm video is handled in `convertFloatingRoomToSticky` as it may need to open a quick reply
            const hiddenProfileVideo =
                modalState.floating_room.roomView === RoomView.Profile &&
                modalState.floating_room.minimized

            if (
                !closeRoom &&
                isModelViewable(modalState.floating_room.id) && // is still actually broadcasting
                !pinnedRooms.includes(modalState.floating_room.id) && // doesn't already have pinned room
                !hiddenProfileVideo && // ensure we don't open video when coming from profile where video is hidden
                !unauthenticatedMessageRequest // if a user opens messages as a guest, do not open floater
            ) {
                route(visibleRoute, replaceState)
                const userWantsPreview =
                    SETTINGSSTATE[SettingsType.DEFAULT].enableFloatingPreview

                motionTransition(
                    () => {
                        if (currentRouteState) {
                            _setRoomOpenState(
                                false,
                                modalState.floating_room.id,
                            )
                            if (!userWantsPreview)
                                updateRoomState(currentRouteID, {
                                    viewing: false,
                                })
                        }
                        convertFloatingRoomToSticky(
                            modalState.floating_room.id,
                            coords,
                        )
                    },
                    {
                        id: usableVideoID,
                        nav: !initial,
                        skipTransition: !userWantsPreview || !!coords, // only animate if user even wants it
                        transitionSource: TransitionTypes.ROOM,
                        transitionDestination: TransitionTypes.ROOM,
                    },
                )
            } else {
                route(visibleRoute, replaceState)
                motionTransition(
                    () => {
                        // this will safely handle if a room is pinned
                        closeFloatingRoom(modalState.floating_room.id)
                        setFullWindow(false)
                    },
                    {
                        id: usableVideoID,
                        nav: !initial,
                        transitionSource: TransitionTypes.ROOM,
                        transitionDestination:
                            AppRouter.route.lastTransitionSource,
                    },
                )
            }
        }
        return true
    }
    return false
}

const handleGeneralRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, pendingSlug } = routeInfo
    const { closeRoom = false, coords, replaceState, initial } = options

    if (
        nonRoomShrinkCheck(fullRoute, routeInfo, {
            closeRoom,
            coords,
            replaceState,
            initial,
        })
    )
        return

    if (pendingSlug) {
        await loadUserIfNeeded(pendingSlug)
        AppRouter.route.slugID = slugToID[pendingSlug]
    }

    // console.log('[handleGeneralRouting]', fullRoute)
    return motionTransition(() => route(fullRoute, replaceState), {
        nav: !initial,
    })
}

const resolveSearchPath = (
    path: string,
    search: string,
    previousPath: string,
) => {
    if (path === '/search' && !search) {
        if (
            SEARCHSTATE.query &&
            SEARCHSTATE.query.search &&
            !previousPath.startsWith('/search')
        ) {
            const query = '?query=' + SEARCHSTATE.query.search
            const type = '&type=' + SEARCHSTATE.query.type
            //TODO: this should maybe be driven by the query urls and allow the router to flow these back
            AppRouter.route.baseQuery.query = SEARCHSTATE.query.search
            AppRouter.route.baseQuery.type = SEARCHSTATE.query.type
            return path + query + type
        }

        if (SEARCHSTATE.query && SEARCHSTATE.query.view) {
            AppRouter.route.baseQuery.view = SEARCHSTATE.query.view
            return path + '?view=' + SEARCHSTATE.query.view
        }
    }

    return path
}

const handleChatArchivesRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { skipPreload, initial } = options
    const { pendingQuery, fullRoute } = routeInfo
    const { id } = pendingQuery

    resetArchiveState(id ? Number(id) : null)

    await waitForInitialModelList()
    if (!skipPreload)
        // if we ran this during preload lets skip this
        workerArchive(id ? { id: Number(id) } : null)

    const { closeRoom = false, coords, replaceState } = options

    if (
        nonRoomShrinkCheck(fullRoute, routeInfo, {
            closeRoom,
            coords,
            replaceState,
            initial,
        })
    )
        return

    // await route(fullRoute, replaceState)
    return motionTransition(() => route(fullRoute, replaceState), {
        nav: !initial,
    })
}

const handleSearchRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, pendingQuery, currentRoute, currentQuery } = routeInfo
    const { view, type, query: search } = pendingQuery as URLSearchParam
    const { closeRoom = false, coords, replaceState, initial } = options

    const [, { visibleRoute: currentVisibleRoute = '' } = {}] =
        HISTORY.lastRoute
    const isMessagePage = currentRoute === 'message'
    const isRoomPage = currentRoute === 'room'
    if (search) {
        updateSearchQuery({ search, type })

        if (
            currentRoute === 'search' &&
            !isMessagePage &&
            !isRoomPage &&
            !type
        ) {
            return
        }
    }

    if (currentRoute === 'search' && !currentQuery.query && !search) {
        updateSearchView({ view })
    }

    if (
        fullRoute.startsWith('/search') &&
        !isMessagePage &&
        !isRoomPage &&
        !search &&
        currentQuery.query
    ) {
        resetSearch(SEARCHSTATE.query.view)
    }

    const resolvedPath = resolveSearchPath(
        fullRoute,
        search,
        currentVisibleRoute,
    )
    // ensure that the visible route is stored and updated correctly,
    // because we could have restored query values
    AppRouter.route.visibleRoute = resolvedPath

    if (
        nonRoomShrinkCheck(resolvedPath, routeInfo, {
            closeRoom,
            coords,
            replaceState,
            initial,
        })
    )
        return

    // await route(resolvedPath, replaceState)
    return motionTransition(() => route(resolvedPath, replaceState), {
        nav: !initial,
    })
}

const handleProfileRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, pendingRoute, currentRoute, pendingQuery } = routeInfo
    const { slug: ssrSlug } = pendingQuery
    const pendingSlug = ssrSlug || pendingRoute
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile
    // get pending route's id
    const pendingRouteID = pendingSlug ? slugToID[pendingSlug] : null
    const isPendingProfileLounge = isLounge(pendingSlug)
    const foundUser = await loadUserIfNeeded(pendingSlug, options.initial)
    if (!foundUser)
        return route('/error?user=' + pendingSlug, options.replaceState)
    AppRouter.route.slugID = pendingRouteID
    AppRouter.route.slug = pendingSlug

    // console.log(
    //     '[handleProfileRouting]',
    //     {
    //         pendingRouteID,
    //         pendingSlug,
    //         pendingQuery,
    //         // routePendingSlug,
    //         // path,
    //         // asPath,
    //         currentRoute,
    //         isCurrentRouteRoom,
    //     },
    //     options,
    // )

    // only try to close if we have a pinned and we're on desktop, as PMs don't have proper
    // passiveMode handling and on desktop PMs can share space with pinned rooms
    if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID) && !isClientMobile()) {
        pinRoom(pendingRouteID, { pinned: false })
    }

    if (options.initial) {
        if (!isPendingProfileLounge) initVideo(pendingRouteID)
        // open the room a bit early on direct load so chat/video
        // can get connected earlier and settled quicker
        openFloatingRoom(pendingRouteID, RoomView.Profile)
        return route(fullRoute, options.replaceState)
    }
    // ensure video is started directly by a user action for iOS
    if (!isPendingProfileLounge) initVideo(pendingRouteID)

    await motionTransition(
        () => {
            if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID)) {
                pinRoom(pendingRouteID, { pinned: false, skipClose: true })
            }
            openFloatingRoom(pendingRouteID, RoomView.Profile)
        },
        {
            id: pendingRouteID,
            nav: !options.initial,
            skipTransition: isCurrentRouteFloater,
            transitionSource: isCurrentRouteFloater
                ? TransitionTypes.ROOM
                : options.transitionSource,
            transitionDestination: TransitionTypes.ROOM,
        },
    )
    return route(fullRoute, options.replaceState)
}
const handlePmRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const { fullRoute, currentRoute, pendingSlug } = routeInfo

    // check if current route is room
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile

    const foundUser = await loadUserIfNeeded(pendingSlug, options.initial)
    if (!foundUser)
        return route('/error?user=' + pendingSlug, options.replaceState)

    // get pending route's id
    const pendingRouteID = pendingSlug ? slugToID[pendingSlug] : null
    AppRouter.route.slugID = pendingRouteID

    // console.log('[handlePmRouting]', {
    //     pendingRouteID,
    //     path,
    //     asPath,
    //     currentRoute,
    //     isCurrentRouteRoom,
    // })

    // only try to close if we have a pinned and we're on desktop, as PMs don't have proper
    // passiveMode handling and on desktop PMs can share space with pinned rooms
    if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID) && !isClientMobile()) {
        pinRoom(pendingRouteID, { pinned: false })
    }

    if (!pendingRouteID) {
        // update the router state
        const foundBaseRoute = routeConfigs.get('messages')
        AppRouter.route.basePage = 'messages'
        AppRouter.route.baseRoute = '/messages'
        AppRouter.route.baseComponent = foundBaseRoute.component
        AppRouter.route.floatingPage = null
        AppRouter.route.floatingRoute = null
        AppRouter.route.floatingComponent = null
        return route('/messages', options.replaceState)
    }

    const isAuthenticated = authenticated()

    if (isAuthenticated) {
        await openPm(pendingSlug)
    }

    if (options.initial) {
        // open the room a bit early on direct load so chat/video
        // can get connected earlier and settled quicker
        openFloatingRoom(pendingRouteID, RoomView.PM)
        await waitForInitialModelList()
        route(fullRoute, options.replaceState)
        if (isAuthenticated) initVideo(pendingRouteID)
        pausePMUnreadCount(pendingRouteID)
        setPMChatOpenState(pendingRouteID, true)
        return
    }

    if (isAuthenticated) {
        // ensure video is started directly by a user action for iOS
        initVideo(pendingRouteID)
    }

    await motionTransition(
        () => {
            if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID)) {
                pinRoom(pendingRouteID, { pinned: false, skipClose: true })
            }
            openFloatingRoom(pendingRouteID, RoomView.PM)
            pausePMUnreadCount(pendingRouteID)
            setPMChatOpenState(pendingRouteID, true)
        },
        {
            id: pendingRouteID,
            nav: !options.initial,
            skipTransition: isCurrentRouteFloater,
            transitionSource: isCurrentRouteFloater
                ? TransitionTypes.ROOM
                : options.transitionSource,
            transitionDestination: TransitionTypes.ROOM,
        },
    )

    return route(fullRoute, options.replaceState)
}

const handleRoomRouting: RouteHandler = async (
    routeInfo: RouteInfo,
    options: RouteHandlerOptions,
) => {
    const {
        fullRoute,
        currentRoute,
        pendingSlug,
        currentSlug,
        pendingSurrogateSlug,
    } = routeInfo

    // check if current route is room
    const isCurrentRouteRoom = currentRoute === 'room'
    const isCurrentRoutePm = currentRoute === 'message'
    const isCurrentRouteProfile = !!slugToID[currentRoute]
    const isCurrentRouteFloater =
        isCurrentRouteRoom || isCurrentRoutePm || isCurrentRouteProfile
    const isPendingRoomLounge = isLounge(pendingSlug)

    if (isPendingRoomLounge) {
        await waitForInitialModelList()
    }

    const foundUser = await loadUserIfNeeded(pendingSlug, options.initial)
    if (!foundUser)
        return route('/error?user=' + pendingSlug, options.replaceState)

    const pendingRouteID = pendingSlug ? slugToID[pendingSlug] : null
    AppRouter.route.slugID = pendingRouteID
    // if we're navigating room to room (next/prev, available space, etc)
    if (isCurrentRouteRoom && currentSlug && currentSlug !== pendingSlug) {
        // ensure this sets the viewing state
        updateRoomState(slugToID[currentSlug], {
            viewing: false,
        })
    }

    const { [pendingRouteID]: pendingRouteState } = getRoomsState()
    // if user is navigating to room (doesn't matter from where) an
    // state does not have the room
    if (!pendingRouteState || pendingRouteState.isOpen === undefined) {
        // initiate new room state
        _initiateNewRoom(pendingRouteID)
    }
    const newPendingRouteState = getRoomsState()[pendingRouteID]

    // if we are opening a room and it exists
    if (newPendingRouteState.isOpen !== undefined) {
        // set room to open
        _setRoomOpenState(true, pendingRouteID)
    }
    if (isPendingRoomLounge) {
        // make sure we set the room state to lounge before we do anything else
        setRoomLoungeState(true, pendingRouteID)

        // we have to delay lounge load a tiny bit so ME user is populated
        const loggedIn = await meUser

        const { rewardsPoints, level } = loggedIn
            ? USERSSTATE[slugToID['me']]
            : { rewardsPoints: 0, level: 0 }

        const loungesWithAccess = enabledLounges(rewardsPoints, level)
        const hasSpecifiedLoungeAccess = loungesWithAccess?.includes(
            pendingSlug as any,
        )

        if (!hasSpecifiedLoungeAccess) {
            route(fullRoute, options.replaceState)
            motionTransition(() => openFloatingRoom(pendingRouteID), {
                id: pendingRouteID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: options.transitionSource,
                transitionDestination: TransitionTypes.ROOM,
            })
            return
        }

        const { loungeSurrogateID } = newPendingRouteState
        if (pendingSurrogateSlug) {
            await loadUserIfNeeded(pendingSurrogateSlug, options.initial)
        }

        const pendingSurrogateID = pendingSurrogateSlug
            ? slugToID[pendingSurrogateSlug]
            : loungeSurrogateID
        const foundSurrogateID =
            getSafeNextOrPreviousSurrogate(pendingSurrogateID)
        AppRouter.route.surrogateID = foundSurrogateID

        // ensure video init is called
        initVideo(foundSurrogateID)
        startAutoPlayInterval(pendingRouteID)
        addToLoungeRecentModelsSession(foundSurrogateID)

        const fullRoomRoute = buildRoomRoute(pendingRouteID, foundSurrogateID)
        setRoomLoungeSurrogateState(foundSurrogateID, pendingRouteID)

        if (isCurrentRouteRoom) {
            // room to lounge
            route(fullRoomRoute, options.replaceState)
            motionTransition(() => openFloatingRoom(pendingRouteID), {
                id: foundSurrogateID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: TransitionTypes.ROOM,
                transitionDestination: TransitionTypes.ROOM,
            })
            // activateTransform(foundSurrogateID)
        } else {
            // non-room to lounge
            await motionTransition(() => openFloatingRoom(pendingRouteID), {
                id: foundSurrogateID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: options.transitionSource,
                transitionDestination: TransitionTypes.ROOM,
            })
            // activateTransform(foundSurrogateID)
            return route(fullRoomRoute, options.replaceState)
        }
        return
    }
    if (pendingRouteID) {
        if (options.initial) {
            initVideo(pendingRouteID)
            // open the room a bit early on direct load so chat/video
            // can get connected earlier and settled quicker
            openFloatingRoom(pendingRouteID)
            return route(fullRoute, options.replaceState)
        }
        // ensure video is started directly by a user action for iOS
        initVideo(pendingRouteID)
        await motionTransition(
            () => {
                if (ROOMSSTATE.pinnedRooms.includes(pendingRouteID)) {
                    pinRoom(pendingRouteID, { pinned: false, skipClose: true })
                }
                openFloatingRoom(pendingRouteID)
            },
            {
                id: pendingRouteID,
                nav: !options.initial,
                skipTransition: isCurrentRouteFloater,
                transitionSource: isCurrentRouteFloater
                    ? TransitionTypes.ROOM
                    : options.transitionSource,
                transitionDestination: TransitionTypes.ROOM,
            },
        )
        // activateTransform(pendingRouteID)
        return route(fullRoute, options.replaceState)
    }

    // await route(fullRoute, options.replaceState)
    return motionTransition(() => route(fullRoute, options.replaceState), {
        nav: !options.initial,
        transitionDestination: TransitionTypes.ROOM,
    })
}

/**
 * A "smart" wrapper for `route()` calls that is aware of our app.
 *
 * Work towards controlling our routing a bit more by being more proactive about room handling
 * in routing.  Our router should ideally be aware of our overlayed room, as well as video, and
 * be able to leverage view transition. In the meantime, just handle it in the `push` and pretend
 * like we have control
 *
 * @param visibleRoute - the visible component/page (whether top layer or base layer)
 * @param options.baseRoute (optional) - specify the `baseRoute` if `visibleRoute` is a top layer
 * @param options.closeRoom (optional) close the room
 * @param options.initial (optional) initial route from ssr load
 * @param options.transitionSource (optional) source for the transition to start from
 * @param options.replaceState (optional) use replaceState instead of pushState (back/forward nav handling)
 * @param options.coords (optional) coordinates for room preview sticky
 * @param options.skipPreload (optional) skip the route preload function
 */
export const routerPush = async (
    visibleRoute: string,
    options: RouteHandlerOptions = {},
) => {
    const {
        initial = false,
        baseRoute = visibleRoute,
        skipPreload = false,
        replaceState = false,
    } = options

    // we need to do a few things if we have a room open
    // get current visible route
    const [currentRoute, currentSlug, currentSurrogateSlug, currentQuery] =
        getRouteSegments(AppRouter.route.visibleRoute)

    // get pending visible route
    const [
        pendingVisiblePage,
        pendingVisibleSlug,
        pendingVisibleSurrogateSlug,
        pendingVisibleQuery,
        pendingVisibleHash,
        pendingFullVisibleRoute,
    ] = getRouteSegments(visibleRoute)

    if (pendingVisibleQuery) {
        AppRouter.route.visibleQuery = pendingVisibleQuery
    }
    AppRouter.route.hash = pendingVisibleHash

    const foundVisibleRoute = routeConfigs.get(pendingVisiblePage)
    if (!foundVisibleRoute) {
        route('/error', replaceState)
    }

    if (foundVisibleRoute.floater) {
        AppRouter.route.floatingPage = pendingVisiblePage
        AppRouter.route.floatingRoute = pendingFullVisibleRoute
        AppRouter.route.floatingComponent = foundVisibleRoute.component
    }
    AppRouter.route.slug = pendingVisibleSlug
    AppRouter.route.surrogateSlug = pendingVisibleSurrogateSlug

    AppRouter.route.visiblePage = pendingVisiblePage
    AppRouter.route.visibleRoute = visibleRoute

    // get the pending base route
    const [
        pendingBasePage = '',
        ,
        ,
        pendingBaseQuery,
        ,
        pendingFullBaseRoute = '/',
    ] = getRouteSegments(baseRoute)
    const foundBaseRoute = routeConfigs.get(pendingBasePage)
    if (!foundBaseRoute.floater) {
        AppRouter.route.basePage = pendingBasePage
        AppRouter.route.baseRoute = pendingFullBaseRoute
        AppRouter.route.baseComponent = foundBaseRoute.component
        AppRouter.route.baseQuery = pendingBaseQuery
        /// this should never actually be necessary, as all base routes get set by
        /// natural navigation so a preload would have been run on visible route
        /// in the past, so just hold off on this
        // run the base route preload
        // if (
        //     !skipPreload &&
        //     foundBaseRoute !== foundVisibleRoute &&
        //     !foundBaseRoute.skipRouterPreload
        // )
        //     await foundBaseRoute.preload?.()
    }

    if (
        !skipPreload &&
        !foundVisibleRoute.skipRouterPreload &&
        foundVisibleRoute.preload
    ) {
        // run the preload
        if (foundVisibleRoute.skipAwait) {
            if (foundVisibleRoute.floater)
                AppRouter.route.preloadFloatingData =
                    foundVisibleRoute.preload()
            else AppRouter.route.preloadBaseData = foundVisibleRoute.preload()
        } else {
            if (foundVisibleRoute.floater)
                AppRouter.route.preloadFloatingData =
                    await foundVisibleRoute.preload()
            else
                AppRouter.route.preloadBaseData =
                    await foundVisibleRoute.preload()
        }
    }

    // this check won't take into account a baseRoute only change, but im not sure we need to ever do that
    if (
        currentRoute === pendingVisiblePage &&
        currentSlug === pendingVisibleSlug &&
        currentSurrogateSlug === pendingVisibleSurrogateSlug &&
        currentQuery === pendingVisibleQuery &&
        !initial
    ) {
        return
    }

    const isProfile =
        pendingFullVisibleRoute.startsWith('/profile') ||
        (pendingVisiblePage && slugToID[pendingVisiblePage])

    const routeInfo = {
        currentRoute,
        pendingRoute: pendingVisiblePage,
        currentQuery,
        pendingQuery: pendingVisibleQuery,
        currentSlug,
        pendingSlug: pendingVisibleSlug,
        currentSurrogateSlug,
        pendingSurrogateSlug: pendingVisibleSurrogateSlug,
        // route: foundVisibleRoute,
        fullRoute: pendingFullVisibleRoute,
    }

    // console.log(
    //     '[routerPush]',
    //     AppRouter.route,
    //     routeInfo,
    //     {
    //         isProfile,
    //         baseRoute,
    //         visibleRoute,
    //     },
    //     options,
    // )

    // Route change start
    AppRouter.events.emit('change', { routeInfo, initial })

    // Routing in process.
    // the route state can be changed here and it won't be guaranteed to be finalized til `completed`
    // should we delay the change event til after these run then?
    switch (pendingVisiblePage) {
        case 'room':
            await handleRoomRouting(routeInfo, options)
            break
        case 'message':
            await handlePmRouting(routeInfo, options)
            break
        case 'search':
            await handleSearchRouting(routeInfo, options)
            break
        case 'chat-archives':
            await handleChatArchivesRouting(routeInfo, options)
            break
        default:
            if (isProfile) {
                await handleProfileRouting(routeInfo, options)
                break
            }
            await handleGeneralRouting(routeInfo, options)
    }

    AppRouter.events.emit('completed', { routeInfo, initial }, false)
}

// console.log('[router running]', AppRouter)
if (typeof window !== 'undefined')
    AppRouter.init(document.location.href.replace(document.location.origin, ''))
